// Custom frame support (Canva-style). Any shape/SVG can become a frame by
// supplying a clip path. A frame's clip is unified here so built-in frames
// (normalized 0..1 paths from frameShapes) and custom frames (a path in its own
// viewBox) render through one code path. The renderer scales the path by
// width/vbW and height/vbH, so a frame resizes its clip with its box.

import { getFrameShape } from './frameShapes';

// Cache clip descriptors so repeated renders (100+ frames, drag) reuse the same
// object — cheap to build, but stable identity helps React skip work. Keyed by
// the path source; bounded so it can't grow without limit.
const clipCache = new Map();
const CLIP_CACHE_MAX = 400;
function cachedClip(key, build) {
  let v = clipCache.get(key);
  if (!v) {
    v = build();
    if (clipCache.size >= CLIP_CACHE_MAX) clipCache.clear();
    clipCache.set(key, v);
  }
  return v;
}

// Returns { path, vbW, vbH } for any frame element.
//   custom  → element.framePath in element.frameViewBox space
//   builtin → normalized 0..1 path (vb 1x1)
export function getFrameClip(element) {
  if (element && element.framePath) {
    const vb = element.frameViewBox || { w: 1, h: 1 };
    const vbW = Math.max(0.0001, vb.w || 1);
    const vbH = Math.max(0.0001, vb.h || 1);
    return cachedClip(`c:${element.framePath}:${vbW}:${vbH}`, () => ({ path: element.framePath, vbW, vbH }));
  }
  const shapeId = (element && element.shape) || 'rectangle';
  return cachedClip(`b:${shapeId}`, () => ({ path: getFrameShape(shapeId).path, vbW: 1, vbH: 1 }));
}

// Path string (in 0..w / 0..h space) for the shapes we can turn into frames.
// Anything we can't trace falls back to the bounding rectangle.
export function shapeToPath(shapeType, w, h) {
  const W = Math.max(1, Number(w) || 1);
  const H = Math.max(1, Number(h) || 1);
  const rect = `M0,0 H${W} V${H} H0 Z`;
  switch (shapeType) {
    case 'rect':
    case 'rectangle':
      return rect;
    case 'roundedRect': {
      const r = Math.min(W, H) * 0.12;
      return `M${r},0 H${W - r} Q${W},0 ${W},${r} V${H - r} Q${W},${H} ${W - r},${H} H${r} Q0,${H} 0,${H - r} V${r} Q0,0 ${r},0 Z`;
    }
    case 'circle':
    case 'ellipse':
      return `M${W / 2},0 A${W / 2},${H / 2} 0 1 1 ${W / 2},${H} A${W / 2},${H / 2} 0 1 1 ${W / 2},0 Z`;
    case 'triangle':
      return `M${W / 2},0 L${W},${H} L0,${H} Z`;
    case 'diamond':
      return `M${W / 2},0 L${W},${H / 2} L${W / 2},${H} L0,${H / 2} Z`;
    case 'pentagon':
      return `M${W / 2},0 L${W},${0.38 * H} L${0.81 * W},${H} L${0.19 * W},${H} L0,${0.38 * H} Z`;
    case 'hexagon':
      return `M${0.25 * W},0 L${0.75 * W},0 L${W},${H / 2} L${0.75 * W},${H} L${0.25 * W},${H} L0,${H / 2} Z`;
    case 'octagon': {
      const a = 0.29;
      return `M${a * W},0 L${(1 - a) * W},0 L${W},${a * H} L${W},${(1 - a) * H} L${(1 - a) * W},${H} L${a * W},${H} L0,${(1 - a) * H} L0,${a * H} Z`;
    }
    case 'star': {
      const cx = W / 2, cy = H / 2, or = Math.min(W, H) / 2, ir = or * 0.4;
      let d = '';
      for (let i = 0; i < 10; i++) {
        const r = i % 2 === 0 ? or : ir;
        const ang = (Math.PI / 5) * i - Math.PI / 2;
        const x = cx + r * Math.cos(ang) * (W / Math.min(W, H));
        const y = cy + r * Math.sin(ang) * (H / Math.min(W, H));
        d += `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)} `;
      }
      return d + 'Z';
    }
    case 'heart':
      return `M${W / 2},${H} C${0.2 * W},${0.78 * H} 0,${0.6 * H} 0,${0.32 * H} A${0.26 * W},${0.26 * H} 0 0 1 ${W / 2},${0.22 * H} A${0.26 * W},${0.26 * H} 0 0 1 ${W},${0.32 * H} C${W},${0.6 * H} ${0.8 * W},${0.78 * H} ${W / 2},${H} Z`;
    default:
      return rect;
  }
}

// Extract a clip path + viewBox from raw SVG markup (first <path> or <polygon>).
export function svgToFramePath(svgContent) {
  if (typeof svgContent !== 'string') return null;
  let vbW = 100, vbH = 100;
  const vb = svgContent.match(/viewBox=["']\s*([\d.eE+\-]+)\s+([\d.eE+\-]+)\s+([\d.eE+\-]+)\s+([\d.eE+\-]+)\s*["']/);
  if (vb) { vbW = Number(vb[3]) || vbW; vbH = Number(vb[4]) || vbH; }
  const pathMatch = svgContent.match(/\bd=["']([^"']+)["']/);
  if (pathMatch) return { path: pathMatch[1], vbW, vbH };
  const poly = svgContent.match(/<polygon[^>]*points=["']([^"']+)["']/i);
  if (poly) {
    const pts = poly[1].trim().split(/\s+/);
    return { path: `M${pts.join(' L')} Z`, vbW, vbH };
  }
  return null;
}

const FRAMEABLE = new Set(['shape', 'svg', 'sticker', 'image', 'penPath', 'drawing']);

// Does the element carry its own vector geometry (Pen Tool / imported path)?
export function hasVectorGeometry(element) {
  return !!element && (
    !!element.path || !!element.svgPath || !!element.vectorPath
    || (Array.isArray(element.points) && element.points.length >= 2)
  );
}

const pathEndsClosed = (d) => typeof d === 'string' && /z\s*$/i.test(d.trim());

// A vector shape can only become a frame if it is CLOSED. Closure is detected
// from an explicit `closed` flag, a trailing Z in the path, or a first≈last
// point coincidence.
export function isClosedVector(element) {
  if (!element) return false;
  if (element.closed === true) return true;
  if (pathEndsClosed(element.path || element.svgPath || element.vectorPath)) return true;
  const pts = element.points;
  if (Array.isArray(pts) && pts.length >= 3) {
    const a = pts[0];
    const b = pts[pts.length - 1];
    if (a && b && Math.hypot((a.x || 0) - (b.x || 0), (a.y || 0) - (b.y || 0)) < 1.5) return true;
  }
  return false;
}

function pointsToPath(points, close) {
  const d = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
  return close ? `${d} Z` : d;
}

// Extract a closed clip path (in element-local 0..w/0..h space) from a vector
// element's own geometry. Returns null when there is no usable geometry.
export function getVectorFramePath(element) {
  const w = Math.max(1, Number(element.width) || 1);
  const h = Math.max(1, Number(element.height) || 1);
  let d = element.path || element.svgPath || element.vectorPath || null;
  if (!d && Array.isArray(element.points) && element.points.length >= 2) {
    d = pointsToPath(element.points, true);
  }
  if (!d) return null;
  if (!pathEndsClosed(d)) d = `${d.trim()} Z`;
  return { path: d, vbW: w, vbH: h };
}

export function canConvertToFrame(element) {
  if (!element || element.type === 'frame') return false;
  return FRAMEABLE.has(element.type) || hasVectorGeometry(element);
}

// Convert a shape / SVG / Pen-Tool vector element into a custom frame,
// preserving geometry, styling, effects, and identity. Returns the full
// replacement element — or null when the element is an OPEN vector path (those
// cannot become frames; the caller should warn).
export function convertElementToFrame(element) {
  if (!element) return null;
  const w = Math.max(1, Number(element.width) || 1);
  const h = Math.max(1, Number(element.height) || 1);
  let framePath = null;
  let frameViewBox = { w, h };
  let frameSource = element.type;

  // Imported SVG: use its embedded path + viewBox.
  if (element.type === 'svg' && element.svgContent) {
    const ex = svgToFramePath(element.svgContent);
    if (ex) { framePath = ex.path; frameViewBox = { w: ex.vbW, h: ex.vbH }; frameSource = 'svg'; }
  }
  // Pen Tool / vector geometry (path / svgPath / vectorPath / points). Only
  // CLOSED paths qualify — reject open ones.
  if (!framePath && hasVectorGeometry(element)) {
    if (!isClosedVector(element)) return null;
    const vec = getVectorFramePath(element);
    if (vec) { framePath = vec.path; frameViewBox = { w: vec.vbW, h: vec.vbH }; frameSource = 'vector'; }
  }
  if (!framePath && element.type === 'shape') {
    framePath = shapeToPath(element.shape, w, h);
    frameViewBox = { w, h };
    frameSource = 'shape';
  }
  if (!framePath) {
    framePath = `M0,0 H${w} V${h} H0 Z`;
    frameViewBox = { w, h };
  }

  return {
    id: element.id,
    pageId: element.pageId,
    parentId: element.parentId ?? null,
    type: 'frame',
    frameType: 'custom',
    frameSource,
    framePath,
    frameViewBox,
    shape: 'custom',
    x: element.x,
    y: element.y,
    width: element.width,
    height: element.height,
    rotation: element.rotation || 0,
    flipX: !!element.flipX,
    flipY: !!element.flipY,
    opacity: element.opacity ?? 1,
    locked: !!element.locked,
    image: null,
    fit: 'cover',
    // Preserve appearance fields so effects/filters/shadow/blend carry over.
    effect: element.effect ?? null,
    filterPreset: element.filterPreset ?? null,
    shadow: element.shadow ?? null,
    blendMode: element.blendMode ?? null,
  };
}
